import { createFileRoute } from "@tanstack/react-router";
import { PHOTOGRAPHER } from "@/content/foto";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { FotoFilmShowcase } from "@/components/site/foto/FotoFilmShowcase";
import { PhotoRail } from "@/components/site/foto/PhotoRail";
import { coverPhotoFor, galleryPhotos } from "@/content/photo-gallery";
import {
  aeoGeo,
  breadcrumbs,
  imageGallery,
  claim,
  faqPage,
  serviceSchema,
  person,
  speakable,
} from "@/lib/seo/schema";

const COVER = coverPhotoFor("foto-och-film");

const PATH = "/foto/foto-och-film";
const TITLE = "Foto och film samma dag — två leveranser | Fenny";
const DESCRIPTION =
  "Boka fotograf och filmteam samma dag: samma ljus, plats och casting ger både rörligt och stillbilder till lägre totalkostnad. Offert per uppdrag.";

const FAQ: LandingFAQ[] = [
  {
    q: "Varför är det billigare att fotografera och filma samma dag?",
    a: "De stora kostnaderna i en produktion är förberedelse, ljus, plats, casting och restid — och de betalas bara en gång när båda leveranserna produceras samtidigt. Tillägget för en fotograf på en filminspelning är litet jämfört med att göra en separat fotodag.",
  },
  {
    q: "Stör fotograferingen filminspelningen?",
    a: "Nej, om det är planerat. Vi lägger in fotostopp i körschemat mellan tagningarna och fotograferar tyst under rörligt när det går. Det som förstör en dag är en fotograf som dyker upp oplanerat, inte en som står i schemat.",
  },
  {
    q: "Får vi stillbilder ur filmen i stället?",
    a: "Stillbilder ur video går att använda i mindre format, men upplösning, skärpedjup och slutartid är optimerade för rörligt. För tryck, annons och stora ytor rekommenderar vi riktiga stillbilder tagna av en fotograf.",
  },
  {
    q: "Vilka uppdrag passar bäst för kombinationen?",
    a: "Kampanjer, event, rekryteringsmaterial och produktlanseringar — allt där både sajt och sociala kanaler ska fyllas med samma budskap i olika format.",
  },
];

export const Route = createFileRoute("/foto/foto-och-film")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Foto och film samtidigt — Fenny" },
      { property: "og:description", content: "En inspelningsdag som ger både rörligt och stillbilder." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Går det att boka foto och film samma dag och vad sparar man på det?",
            answer:
              "Ja. När foto och film produceras samma dag betalas förberedelse, ljus, plats och casting bara en gång, vilket sänker totalkostnaden jämfört med två separata produktionsdagar. Ni får rörligt för kampanj och stillbilder för sajt, annons och press i samma bildmanér. Fenny bemannar med både filmteam och fotograf i Stockholm, Göteborg, Malmö, Uppsala och Västerås. Offert per uppdrag. Kontakt: Fenny, hey@fenny.se.",
            speakableSelectors: ["[data-quick-answer]"],
          })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Foto och film i samma produktion",
            path: PATH,
            serviceType: "Foto- och filmproduktion",
            description: "Kombinerad foto- och filmproduktion: samma ljus, plats och casting ger två leveranser.",
            areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          claim({
            text: "När foto och film produceras samma dag betalas förberedelse, ljus, plats och casting bara en gång, vilket sänker totalkostnaden jämfört med två separata produktionsdagar.",
            path: PATH,
            about: "Kombinerad foto- och filmproduktion",
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: TITLE,
            path: PATH,
            description: "Stillbilder från Fennys kombinerade foto- och filmproduktioner.",
            images: galleryPhotos.slice(0, 12),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: PHOTOGRAPHER.email,
            path: PATH,
            description: PHOTOGRAPHER.bio,
            knowsAbout: [
              "Foto och film samma dag",
              "Filmproduktion",
              "Kampanjfoto",
              "Bildbank",
              "Fotograf Stockholm",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Foto och film", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      heroImage={{ src: COVER.src, alt: COVER.alt }}
      eyebrow="Foto + film"
      title={<>En dag. Två leveranser. Ett manér.</>}
      lede="Fenny är i grunden ett filmproduktionsbolag — därför är ljus, plats, casting och schema redan riggat när kameran rullar. Att lägga till en fotograf på samma dag ger er en komplett bildbank vid sidan av filmen, till en bråkdel av vad en separat fotodag kostar."
      tldr={
        <p>
          Boka <strong>foto och film samma dag</strong>: förberedelse, ljus,
          plats och casting betalas en gång. Ni får <strong>rörligt för
          kampanj</strong> och <strong>stillbilder för sajt, annons och
          press</strong> i samma manér. Offert per uppdrag.
        </p>
      }
      ctaPrimary={{ label: "Begär offert", to: "#offert" }}
      ctaSecondary={{ label: "Se filmtjänster", to: "/tjanster" }}
      trackingId="foto_foto_och_film"
      stills={false}
      videos={false}
      afterHero={
        <FotoFilmShowcase
          heading="En dag i två format — se skillnaden"
          lede="Överst rörligt från produktionerna, under stillbilder tagna samma dag i samma ljus och miljö."
          videoCount={3}
          photoCount={6}
        />
      }
      afterSections={
        <PhotoRail
          photos={galleryPhotos.slice(6, 16)}
          eyebrow="Från produktionerna"
          heading="Stillbilder tagna på filminspelningar"
        />
      }
      bullets={[
        { title: "En förberedelse", body: "Koncept, casting, plats och styling görs en gång och används två gånger." },
        { title: "Samma bildspråk", body: "Färg, ljus och ton matchar mellan film och stillbild — kampanjen hänger ihop." },
        { title: "Planerade fotostopp", body: "Fotograferingen ligger i körschemat, inte mellan raderna." },
      ]}
      sections={[
        {
          id: "sa-planeras-dagen",
          heading: "Så planeras en kombinerad dag",
          body: (
            <p>
              Vi delar körschemat i block. Varje block har ett ljus och en plats,
              och avslutas med ett fotostopp på fem till tio minuter där vi tar
              porträtt, situationsbilder och detaljer i samma uppsättning. Vid
              intervjuinspelningar fotograferas dessutom tyst under pågående
              tagning.
            </p>
          ),
        },
        {
          id: "vanliga-kombinationer",
          heading: "Vanliga kombinationer",
          body: (
            <p>
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">Reklamfilm</a>{" "}
              plus kampanjbilder,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/employer-branding-film">employer branding-film</a>{" "}
              plus medarbetarporträtt,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktfilm">produktfilm</a>{" "}
              plus packshots, samt{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm">eventfilm</a>{" "}
              plus eventfoto.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      riskReducers
      closing={{
        title: "Planerar ni en produktion?",
        body: "Berätta vad filmen ska göra så visar vi vad stillbilderna kostar att lägga till.",
        primary: { label: "Begär offert", to: "#offert" },
      }}
      contactSlot={<FotoQuoteForm location="foto_foto_och_film" defaultAssignment="foto-och-film" />}
    />
  );
}
